
import { Calendar } from '@fullcalendar/core';
import Filter from '@/components/calendar/Filter.vue';
import Loading from '@/components/general/Loading.vue';
import EventDetail from '@/components/calendar/EventDetail.vue';
import WdwDynbox from '@/components/general/WdwDynbox.vue';
import { domainStore } from '@/store/domain-store';
import { calendarStore } from '@/store/calendar-store';
import useCalendar from '@/composables/calendar/useCalendar';
import useLang from '@/composables/general/useLang';
import { useRoute } from 'vue-router';
import useHashParams from '@/composables/general/useHashParams';
import { defineComponent, onMounted, ref, onBeforeMount, computed, watch, nextTick, toRef } from 'vue';
import useAuthentication from '@/composables/user/useAuthentication';

export default defineComponent({
  name: 'Calendar',
  components: {
    Filter,
    WdwDynbox,
    EventDetail,
    Loading,
  },

  props: {
    domainid: {
      type: Number,
      required: true,
    },
    eventid: {
      type: Number,
      required: false,
    },
    privacyPageUrl: {
      type: String,
      required: false,
      default: '',
    },
    cal_config: {
      type: Object,
      required: false,
      default: () => {
        return {
          show_filter: true,
          default_filter: {},
          calendar_view: 'dayGridMonth',
          parent_height: false,
        };
      },
    },
    lang: {
      type: String,
      required: false,
      default: 'de',
    },
  },

  setup(props) {
    const route = useRoute();
    const { setHashParams, deleteHashParams } = useHashParams(route);
    const { initCalendar } = useCalendar(route);
    const {} = useAuthentication();
    const lang = toRef(props, 'lang');
    useLang(lang);
    const calendarEl = ref<HTMLDivElement>();
    const calendar = ref<Calendar>();

    //computed
    const showEventDetail = computed((): boolean => {
      return calendarStore.getState().showEventDetail;
    });
    const isInitiated = computed((): boolean => {
      return calendarStore.getState().isInitiated;
    });
    const isLoadingContent = computed((): boolean => {
      return calendarStore.getState().isLoadingContent;
    });
    const isFilterMinimized = computed((): boolean => {
      return calendarStore.getState().isFilterMinimized;
    });
    const showFilter = computed((): boolean => {
      if (props.cal_config.show_filter !== undefined) {
        return props.cal_config.show_filter;
      }
      return true;
    });

    //methods
    const closeEventDetail = () => {
      calendarStore.showEventDetail(false);
      deleteHashParams(['cal_eventid']);
      nextTick(function () {
        calendar.value?.changeView(calendar.value.view.type);
      });
    };
    const isEmpty = (value: any): boolean => {
      if (value === '' || value === undefined || value === null || Object.entries(value).length === 0) {
        return true;
      } else return false;
    };

    //lifehooks
    onBeforeMount(() => {
      domainStore.initDomain(props.domainid, props.lang);
      calendarStore.setConfig();
    });

    onMounted(() => {
      if (!calendarEl.value) return console.error('DOM-Element could not be initialized');

      //set event, if prop is set
      if (props.eventid) {
        calendarStore.getSingleEvent(props.eventid);
        calendarStore.showEventDetail(true);
        setHashParams({ cal_eventid: props.eventid });
      }

      //initiate FullCalendar
      //NOTE:
      // check parent_height config when integrating into the website
      calendar.value = initCalendar(calendarEl.value, {
        initialView: props.cal_config?.calendar_view,
        parent_height: props.cal_config?.parent_height,
      });

      //render FullCalendar
      calendar.value.render();
      if (!calendar.value) {
        return console.error('Calendar could not be initialized');
      }
      calendarStore.isInitiated(true);

      // set default filter, if props is set
      if (
        !props.eventid &&
        props.cal_config?.default_filter &&
        Object.keys(props.cal_config.default_filter).length > 0
      ) {
        calendarStore.isFilterMinimized(false);
        calendarStore.updateEvents(calendar.value, {
          start: props.cal_config.default_filter.startDate,
          end: props.cal_config.default_filter.endDate,
          text: props.cal_config.default_filter.filterText,
        });
      }
    });

    //watch for query param because it isnt set in onMounted-lifehook yet
    watch(
      () => route.query,
      () => {
        if (route.query.cal_eventid) {
          //NOTE:
          // needs to be in promise, else attendanceMessage wont get filled in the Attendance-Component
          calendarStore.getSingleEvent(Number(route.query.cal_eventid)).then(() => {
            calendarStore.showEventDetail(true);
            if (route.query.cal_verifytoken) {
              calendarStore.verifyToken(
                Number(route.query.cal_eventid),
                Number(route.query.cal_attendanceid),
                String(route.query.cal_verifytoken)
              );
              deleteHashParams(['cal_verifytoken', 'cal_attendanceid']);
              return;
            }
          });
        }
        if (
          props.cal_config.show_filter === false &&
          (route.query.cal_startDate || route.query.cal_endDate || route.query.cal_filterText)
        ) {
          deleteHashParams(['cal_startDate', 'cal_endDate', 'cal_filterText']);
          return;
        }

        if (
          props.cal_config.show_filter !== false &&
          (!isEmpty(route.query.cal_startDate) ||
            !isEmpty(route.query.cal_endDate) ||
            !isEmpty(route.query.cal_filterText))
        ) {
          calendarStore.updateEvents(calendar.value!, {
            start: route.query.cal_startDate,
            end: route.query.cal_endDate,
            text: route.query.cal_filterText,
          });
        }
      },
      { deep: true }
    );

    return {
      calendar,
      calendarEl,
      showFilter,
      showEventDetail,
      closeEventDetail,
      isInitiated,
      isLoadingContent,
      isFilterMinimized,
    };
  },
});
